import React from "react";
import { useModuleBEM, useUiStyles } from "@b2bportal/core-themes";
import { CoreUiComponents } from "@b2bportal/core-types";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { DialogHeader } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./Dialog.module.scss";

export interface DialogContentComponentProps
  extends Omit<DialogPrimitive.DialogContentProps, "title">,
    React.RefAttributes<HTMLDivElement> {
  /**
   * @deprecated
   * This property is deprecated and will be removed in a future version.
   */
  onClose?: () => void;
  title?: React.ReactNode;
  titleIsVisuallyHidden?: boolean;
  description?: string;
  fullScreen?: boolean;
  className?: string;
  /**
   * @deprecated
   * This property is deprecated and will be removed in a future version.
   * Reason: DialogContent should not be aware of special use cases.
   *
   */
  showBackButton?: boolean;
  showCloseButton?: boolean;
  header?: React.ReactNode;
  bottomFixedContent?: React.ReactNode;
  /**
   * Dialog overlay is controlled by if dialog is modal or not.
   * Prop to override that to show overlay when its not a modal.
   */
  showOverlay?: boolean;
  /**
   * @deprecated
   * This property is deprecated and will be removed in a future version.
   * Reason: DialogContent style should not be controlled with props but through css
   * or Style/Component overrides where its being consumed.
   *
   * Show the bottom border of the header section.
   */
  showHeaderBottomBorder?: boolean;
}

export const DialogContent = React.forwardRef<
  HTMLDivElement,
  DialogContentComponentProps
>(({ children, ...props }, forwardedRef) => {
  const styles = useUiStyles(CoreUiComponents.Dialog, defaultStyles);

  const {
    id,
    onClose,
    title,
    titleIsVisuallyHidden,
    description,
    className,
    bottomFixedContent,
    fullScreen = false,
    showCloseButton = true,
    showBackButton = false,
    showOverlay,
    showHeaderBottomBorder = true,
    header,
  } = props;

  const [block, cn] = useModuleBEM(styles, CoreUiComponents.Dialog);

  return (
    <DialogPrimitive.Portal>
      {showOverlay ? (
        <div
          className={clsx(
            cn("overlay", {
              fullScreen: fullScreen,
            })
          )}
        ></div>
      ) : (
        <DialogPrimitive.Overlay
          className={clsx(
            cn("overlay", {
              fullScreen: fullScreen,
            })
          )}
        />
      )}
      <DialogPrimitive.Content
        ref={forwardedRef}
        id={id}
        forceMount={props.forceMount}
        className={clsx(
          cn("root", {
            fullScreen: fullScreen,
          }),
          className
        )}
        {...(description == null ? { "aria-describedby": undefined } : {})}
      >
        {header != null ? (
          header
        ) : (
          <DialogHeader
            showBackButton={showBackButton}
            showCloseButton={showCloseButton}
            title={title}
            titleIsVisuallyHidden={titleIsVisuallyHidden}
            onClose={onClose}
            showHeaderBottomBorder={showHeaderBottomBorder}
          />
        )}

        {children != null && (
          <div className={cn("content")}>
            {description != null ? (
              <DialogPrimitive.Description className={cn("description")}>
                {description}
              </DialogPrimitive.Description>
            ) : null}
            {children}
          </div>
        )}

        {bottomFixedContent != null ? (
          <div className={cn("bottom")}>{bottomFixedContent}</div>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});

export interface DialogProps extends DialogPrimitive.DialogProps {
  onOpen?: () => void;
  onClose?: () => void;
}

export const Dialog = ({ onOpen, onClose, ...props }: DialogProps) => {
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && onOpen) {
      onOpen();
    } else if (!isOpen && onClose) {
      onClose();
    }

    if (props.onOpenChange) {
      props.onOpenChange(isOpen);
    }
  };

  return <DialogPrimitive.Root {...props} onOpenChange={handleOpenChange} />;
};

export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogTitle = DialogPrimitive.Title;
export const DialogDescription = DialogPrimitive.Description;
